/**
 * Registry of supplier order confirmation CSV extraction formats.
 * FE parser sets meta.parserFormat; FE validates allowlist and maps to Materiah supplier by name.
 */

import {
  MATERIAH_SUPPLIER_MATCH_THRESHOLD,
  normalizeSupplierSelectLabel,
} from './quoteSupplierFormats';

export { MATERIAH_SUPPLIER_MATCH_THRESHOLD };

/** CSV file brand; Materiah catalog typically lists Merck for this supplier. */
export const SIGMA_ORDER_CONFIRMATION_BRAND = 'Sigma-Aldrich';

/** Primary Materiah supplier label to match when the file is Sigma-Aldrich. */
export const SIGMA_MATERIAH_SUPPLIER_PRIMARY = 'Merck';

/** Labels / tokens used to score Materiah supplier options for Sigma CSV files. */
export const SIGMA_MATERIAH_MATCH_TOKENS = [
  'merck',
  'sigma-aldrich',
  'sigma aldrich',
  'merck life science',
  'merck group',
];

/**
 * @param {string} text
 * @returns {string}
 */
function normalizeMatchText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {Object} extractedSupplier
 * @returns {boolean}
 */
function sigmaAldrichFingerprintsMatch(extractedSupplier) {
  const name = String(extractedSupplier?.name || '').toLowerCase();
  const sourceType = String(extractedSupplier?.sourceType || '').toLowerCase();

  const nameHit =
    name.includes('sigma-aldrich') ||
    name.includes('sigma aldrich') ||
    name.includes('merck');
  const sourceHit = sourceType.includes('order_confirmation');

  return nameHit || sourceHit;
}

/**
 * @param {{ value: unknown, label?: string }} option
 * @param {Object} extractedSupplier
 * @returns {{ score: number, reason: string }}
 */
/**
 * True when extraction identifies a Sigma-Aldrich order confirmation (maps to Merck in Materiah).
 *
 * @param {Object} extractedSupplier
 * @returns {boolean}
 */
function isSigmaAldrichExtraction(extractedSupplier) {
  const name = normalizeMatchText(extractedSupplier?.name);
  const brand = normalizeMatchText(extractedSupplier?.recognizedBrand);
  return (
    name.includes('sigma') ||
    name.includes('aldrich') ||
    brand.includes('sigma') ||
    brand.includes('aldrich')
  );
}

function scoreSigmaMateriahOption(option, extractedSupplier) {
  const labelNorm = normalizeMatchText(normalizeSupplierSelectLabel(option.label));
  const extractedName = normalizeMatchText(extractedSupplier?.name);
  const sigmaFile = isSigmaAldrichExtraction(extractedSupplier);

  let score = 0;
  const reasons = [];

  if (sigmaFile) {
    if (labelNorm === 'merck' || /^merck\b/.test(labelNorm)) {
      score += 85;
      reasons.push('Sigma-Aldrich file → Merck supplier');
    } else if (labelNorm.includes('merck')) {
      score += 75;
      reasons.push('Sigma-Aldrich file → Merck in label');
    }
    if (/sigma|aldrich/i.test(labelNorm)) {
      score += 45;
      reasons.push('sigma/aldrich in Materiah label');
    }
  }

  if (extractedName && labelNorm) {
    if (labelNorm === extractedName) {
      score += 70;
      reasons.push('exact name');
    } else if (labelNorm.includes(extractedName) || extractedName.includes(labelNorm)) {
      score += 55;
      reasons.push('name overlap');
    }
  }

  for (const token of SIGMA_MATERIAH_MATCH_TOKENS) {
    if (labelNorm.includes(token)) {
      score += 25;
      reasons.push(`token:${token}`);
      break;
    }
  }

  return {
    score: Math.min(100, score),
    reason: reasons.length ? reasons.join(', ') : 'low confidence',
  };
}

/**
 * @typedef {Object} OrderConfirmationFormatDefinition
 * @property {string} formatId
 * @property {string} displayName
 * @property {boolean} supported
 * @property {(extractedSupplier: Object) => boolean} verifyFingerprints
 * @property {(options: Array, extractedSupplier: Object) => { value: unknown, label: string, score: number, matchReason: string }|null} matchMateriahSupplierOption
 */

/** @type {Record<string, OrderConfirmationFormatDefinition>} */
export const ORDER_CONFIRMATION_FORMATS = {
  sigma_aldrich_order_confirmation_csv: {
    formatId: 'sigma_aldrich_order_confirmation_csv',
    displayName: 'Sigma-Aldrich CSV',
    supported: true,
    verifyFingerprints: sigmaAldrichFingerprintsMatch,
    matchMateriahSupplierOption(options, extractedSupplier) {
      const list = Array.isArray(options) ? options : [];
      let best = null;
      const supplierForMatch = {
        ...extractedSupplier,
        name: extractedSupplier?.name || SIGMA_ORDER_CONFIRMATION_BRAND,
        recognizedBrand:
          extractedSupplier?.recognizedBrand || SIGMA_ORDER_CONFIRMATION_BRAND,
        materiahSupplierHint:
          extractedSupplier?.materiahSupplierHint || SIGMA_MATERIAH_SUPPLIER_PRIMARY,
      };

      for (const option of list) {
        if (option?.value == null || option.value === '') continue;
        const { score, reason } = scoreSigmaMateriahOption(option, supplierForMatch);
        if (score < MATERIAH_SUPPLIER_MATCH_THRESHOLD) continue;
        if (!best || score > best.score) {
          best = {
            value: option.value,
            label: normalizeSupplierSelectLabel(option.label) || String(option.label),
            score,
            matchReason: reason,
          };
        }
      }

      return best;
    },
  },
};

/** @type {string[]} */
export const SUPPORTED_ORDER_CONFIRMATION_FORMAT_IDS = Object.values(
  ORDER_CONFIRMATION_FORMATS
)
  .filter(f => f.supported)
  .map(f => f.formatId);

/**
 * @param {string} [formatId]
 * @returns {OrderConfirmationFormatDefinition|null}
 */
export function getOrderConfirmationFormat(formatId) {
  if (!formatId || typeof formatId !== 'string') return null;
  const key = formatId.trim().toLowerCase();
  return ORDER_CONFIRMATION_FORMATS[key] || null;
}

/**
 * Resolve format from extracted payload meta.parserFormat.
 *
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData} extractedData
 * @returns {{ format: OrderConfirmationFormatDefinition|null, formatId: string|null }}
 */
export function resolveOrderConfirmationFormat(extractedData) {
  const raw = extractedData?.meta?.parserFormat;
  const formatId =
    raw == null || String(raw).trim() === '' ? null : String(raw).trim().toLowerCase();
  const format = formatId ? getOrderConfirmationFormat(formatId) : null;
  return { format, formatId };
}

/**
 * @param {string} [formatId]
 * @returns {boolean}
 */
export function isSupportedOrderConfirmationFormat(formatId) {
  if (!formatId) return false;
  return SUPPORTED_ORDER_CONFIRMATION_FORMAT_IDS.includes(
    String(formatId).trim().toLowerCase()
  );
}
